import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, ProgressBar, Row, Spinner } from 'react-bootstrap';
import axios from 'axios';
import Fuse from 'fuse.js';
import { createWorker } from 'tesseract.js';
import Card from '../../components/Card';
import { useLanguage } from '../../i18n/LanguageContext';
import apiUrl from '../../config/api';

const scannerFiltersStorageKey = 'quizify.scanner.filters';

const normalizeText = (value = '') => value
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^\p{L}\p{N}\s]/gu, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const getCorrectAnswers = (answers = []) => answers.filter((answer) => answer.isCorrect);

const getAnswerText = (answer) => String(answer?.text || '').trim();

const toScannerBooleanFlag = (value) => value === true || value === 1 || value === '1';

const scannerKeywordStopWords = new Set([
  'a', 'ad', 'al', 'allo', 'ai', 'che', 'con', 'del', 'della', 'delle', 'degli', 'dei', 'dal', 'dallo',
  'alla', 'alle', 'agli', 'nel', 'nella', 'nelle', 'negli', 'per', 'tra', 'fra', 'una', 'uno', 'gli',
  'le', 'la', 'il', 'lo', 'un', 'in', 'di', 'da', 'e', 'ed', 'i', 'o', 'come', 'cosa', 'quale',
  'quali', 'quando', 'quanto', 'dove', 'sono', 'sia', 'essere', 'anche', 'solo',
]);

const scannerGenericKeywordWords = new Set([
  'sistema', 'sistemi', 'progettazione', 'progetto', 'oggetto', 'oggetti', 'risposta', 'risposte',
  'domanda', 'domande', 'utente', 'utenti', 'interazione', 'interazioni', 'funzione', 'funzioni',
  'funzionalita', 'elemento', 'elementi', 'processo', 'processi', 'modello', 'modelli', 'metodo',
  'metodi', 'caso', 'casi', 'livello', 'livelli', 'prodotto', 'prodotti', 'prestazione', 'prestazioni',
  'analisi', 'risultato', 'risultati',
]);

const getScannerKeywordWords = (value = '') => (
  String(value || '').match(/[\p{L}\p{N}]+/gu)?.map((raw) => ({
    raw,
    normalized: normalizeText(raw),
  })) || []
);

const isReadableScannerKeywordTerm = (term = '') => {
  const words = getScannerKeywordWords(term);

  return term.length <= 80 && words.length > 0 && words.length <= 8;
};

const getScannerKeywordTermQuality = (term = '') => {
  const words = getScannerKeywordWords(term);
  const meaningfulWords = words.filter((word) => (
    word.normalized.length > 2 && !scannerKeywordStopWords.has(word.normalized)
  ));

  if (!words.length || !meaningfulWords.length) {
    return -10;
  }

  const startsWithStopWord = scannerKeywordStopWords.has(words[0].normalized);
  const endsWithStopWord = scannerKeywordStopWords.has(words[words.length - 1].normalized);
  const genericPenalty = meaningfulWords.filter((word) => (
    scannerGenericKeywordWords.has(word.normalized)
  )).length * 0.75;

  return (meaningfulWords.length * 1.2)
    + Math.min(term.length / 30, 1)
    - (startsWithStopWord ? 1.5 : 0)
    - (endsWithStopWord ? 2.5 : 0)
    - genericPenalty;
};

const doScannerKeywordTermsOverlap = (firstTerm = '', secondTerm = '') => {
  const firstWords = getScannerKeywordWords(firstTerm)
    .map((word) => word.normalized)
    .filter((word) => word.length > 2 && !scannerKeywordStopWords.has(word));
  const secondWordSet = new Set(
    getScannerKeywordWords(secondTerm)
      .map((word) => word.normalized)
      .filter((word) => word.length > 2 && !scannerKeywordStopWords.has(word))
  );

  return firstWords.some((word) => secondWordSet.has(word));
};

const dedupeScannerKeywordTerms = (terms = []) => {
  const selectedTerms = [];

  terms
    .map((term) => ({ term, quality: getScannerKeywordTermQuality(term) }))
    .sort((firstTerm, secondTerm) => {
      if (secondTerm.quality !== firstTerm.quality) {
        return secondTerm.quality - firstTerm.quality;
      }

      return secondTerm.term.length - firstTerm.term.length;
    })
    .forEach(({ term }) => {
      if (!selectedTerms.some((selectedTerm) => doScannerKeywordTermsOverlap(selectedTerm, term))) {
        selectedTerms.push(term);
      }
    });

  return selectedTerms;
};

const getScannerKeywordLineValue = (line = '') => line.replace(/^[^:]+:\s*/, '').trim();

const normalizeScannerKeywordTerm = (term = '') => {
  const cleanTerm = String(term || '').trim();
  const normalizedTerm = normalizeText(cleanTerm);

  if (/^nessuna\b/.test(normalizedTerm)) {
    return cleanTerm.match(/[\p{L}\p{N}]+/u)?.[0] || 'Nessuna';
  }

  if (/^none\b/.test(normalizedTerm)) {
    return cleanTerm.match(/[\p{L}\p{N}]+/u)?.[0] || 'None';
  }

  return cleanTerm;
};

const getScannerKeywordTerms = (keywordHint = '') => {
  const cleanHint = String(keywordHint || '').trim();

  if (!cleanHint) {
    return [];
  }

  const lines = cleanHint
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const differenceLines = lines.filter((line) => /^(differenza|difference)\s*:/i.test(line));
  const contextLines = lines.filter((line) => /^(contesto|context)\s*:/i.test(line));
  const selectedLines = differenceLines.length ? differenceLines : contextLines.length ? contextLines : lines;
  const terms = selectedLines
    .flatMap((line) => getScannerKeywordLineValue(line).split(/[,;\n]/))
    .map((term) => normalizeScannerKeywordTerm(term))
    .filter((term) => term.length > 2 && isReadableScannerKeywordTerm(term));

  return dedupeScannerKeywordTerms([...new Set(terms)]);
};

const isWeakScannerAutomaticKeyword = (word) => (
  !word
  || word.normalized.length < 3
  || scannerKeywordStopWords.has(word.normalized)
  || scannerGenericKeywordWords.has(word.normalized)
);

const getScannerAutomaticKeywordTerms = (question = {}) => {
  if (
    String(question.questionType || question.question_type || '').toLowerCase() !== 'closed'
    || toScannerBooleanFlag(question.keywordHintDisabled || question.keyword_hint_disabled)
  ) {
    return [];
  }

  const answers = Array.isArray(question.answers) ? question.answers : [];
  const correctAnswers = getCorrectAnswers(answers);
  const wrongAnswers = answers.filter((answer) => !answer.isCorrect);

  if (!correctAnswers.length || !wrongAnswers.length) {
    return [];
  }

  const correctText = correctAnswers.map(getAnswerText).join(' ');
  const wrongWordSets = wrongAnswers.map((answer) => new Set(
    getScannerKeywordWords(getAnswerText(answer)).map((word) => word.normalized)
  ));
  const correctWords = getScannerKeywordWords(correctText);
  const specialWord = correctWords.find((word) => ['nessuna', 'nessuno', 'none'].includes(word.normalized));

  if (specialWord) {
    return [specialWord.raw];
  }

  const candidatesByWord = new Map();

  correctWords.forEach((word, index) => {
    if (isWeakScannerAutomaticKeyword(word)) {
      return;
    }

    const missingWrongIndexes = wrongWordSets
      .map((wrongWordSet, wrongIndex) => (wrongWordSet.has(word.normalized) ? null : wrongIndex))
      .filter((wrongIndex) => wrongIndex !== null);

    if (!missingWrongIndexes.length || candidatesByWord.has(word.normalized)) {
      return;
    }

    candidatesByWord.set(word.normalized, {
      ...word,
      index,
      missingWrongIndexes,
      score: (missingWrongIndexes.length * 20) + Math.min(word.normalized.length, 18),
    });
  });

  const candidates = [...candidatesByWord.values()]
    .sort((firstCandidate, secondCandidate) => (
      (secondCandidate.score - firstCandidate.score)
      || (firstCandidate.index - secondCandidate.index)
    ));
  const uniqueCandidates = candidates.filter((candidate) => (
    candidate.missingWrongIndexes.length === wrongWordSets.length
  ));

  if (uniqueCandidates.length) {
    return uniqueCandidates
      .slice(0, 4)
      .sort((firstCandidate, secondCandidate) => firstCandidate.index - secondCandidate.index)
      .map((candidate) => candidate.raw);
  }

  const coveredWrongIndexes = new Set();
  const selectedCandidates = [];

  while (coveredWrongIndexes.size < wrongWordSets.length && selectedCandidates.length < 4) {
    const nextCandidate = candidates
      .filter((candidate) => !selectedCandidates.some((selectedCandidate) => (
        selectedCandidate.normalized === candidate.normalized
      )))
      .map((candidate) => ({
        ...candidate,
        newlyCoveredIndexes: candidate.missingWrongIndexes.filter((wrongIndex) => !coveredWrongIndexes.has(wrongIndex)),
      }))
      .filter((candidate) => candidate.newlyCoveredIndexes.length)
      .sort((firstCandidate, secondCandidate) => (
        (secondCandidate.newlyCoveredIndexes.length - firstCandidate.newlyCoveredIndexes.length)
        || (secondCandidate.score - firstCandidate.score)
      ))[0];

    if (!nextCandidate) {
      break;
    }

    selectedCandidates.push(nextCandidate);
    nextCandidate.newlyCoveredIndexes.forEach((wrongIndex) => coveredWrongIndexes.add(wrongIndex));
  }

  return coveredWrongIndexes.size === wrongWordSets.length
    ? selectedCandidates
      .sort((firstCandidate, secondCandidate) => firstCandidate.index - secondCandidate.index)
      .map((candidate) => candidate.raw)
    : [];
};

const escapeScannerKeywordRegex = (value = '') => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getScannerKeywordRegexSource = (term = '') => {
  const keywordWords = getScannerKeywordWords(term).map((word) => word.raw).filter(Boolean);
  const termSource = keywordWords.length > 1
    ? keywordWords.map(escapeScannerKeywordRegex).join('[^\\p{L}\\p{N}]+')
    : escapeScannerKeywordRegex(term.trim());

  return termSource ? `(?<![\\p{L}\\p{N}])${termSource}(?![\\p{L}\\p{N}])` : '';
};

const hasScannerKeywordMatch = (textValue = '', term = '') => {
  const source = getScannerKeywordRegexSource(term);

  return source ? new RegExp(source, 'iu').test(textValue) : false;
};

const renderScannerHighlightedText = (textValue = '', keywordTerms = []) => {
  const safeText = String(textValue || '');
  const matchingTerms = keywordTerms
    .map((term) => ({
      term,
      source: getScannerKeywordRegexSource(term),
    }))
    .filter(({ source, term }) => source && hasScannerKeywordMatch(safeText, term))
    .sort((firstTerm, secondTerm) => secondTerm.term.length - firstTerm.term.length);

  if (!matchingTerms.length) {
    return safeText;
  }

  const keywordRegex = new RegExp(`(${matchingTerms.map(({ source }) => source).join('|')})`, 'giu');

  return safeText.split(keywordRegex).map((part, index) => {
    const matchedKeyword = matchingTerms.find(({ source }) => (
      new RegExp(`^${source}$`, 'iu').test(part)
    ));

    if (!matchedKeyword) {
      return part;
    }

    return (
      <mark key={`${part}-${index}`} className="scanner-keyword-highlight quiz-keyword-highlight-difference">
        {part}
      </mark>
    );
  });
};

const getTokenUserId = (token) => {
  if (!token) {
    return 'guest';
  }

  try {
    const payload = JSON.parse(window.atob(token.split('.')[1]));
    return payload.id || payload.email || 'guest';
  } catch (error) {
    return 'guest';
  }
};

const readStoredScannerFilters = (storageKey) => {
  try {
    return JSON.parse(localStorage.getItem(storageKey) || '{}');
  } catch (error) {
    return {};
  }
};

const setStoredValue = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn('Unable to save scanner filters:', error);
  }
};

const splitRecognizedQuestions = (text) => {
  const cleanText = text.replace(/\r/g, '').trim();

  if (!cleanText) {
    return [];
  }

  const numberedBlocks = cleanText
    .split(/\n(?=\s*(?:\d+[).]|domanda\s+\d+|quesito\s+\d+)\s+)/i)
    .map((block) => block.trim())
    .filter(Boolean);

  const uniqueBlocks = (blocks) => {
    const seenBlocks = new Set();

    return blocks.filter((block) => {
      const normalizedBlock = normalizeText(block);

      if (!normalizedBlock || seenBlocks.has(normalizedBlock)) {
        return false;
      }

      seenBlocks.add(normalizedBlock);
      return true;
    });
  };

  if (numberedBlocks.length > 1) {
    return uniqueBlocks(numberedBlocks);
  }

  return uniqueBlocks([cleanText]);
};

const cleanOcrText = (value = '') => value
  .replace(/\r/g, '')
  .split('\n')
  .map((line) => line
    .replace(/[|_=~]+/g, ' ')
    .replace(/[^\p{L}\p{N}\s.,;:?!'"()àèéìòùÀÈÉÌÒÙ-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s+[a-zA-Z]$/g, '')
    .trim())
  .filter((line) => {
    if (!line) {
      return false;
    }

    const lettersAndNumbers = line.match(/[\p{L}\p{N}]/gu)?.length || 0;
    const symbolCount = line.match(/[^\p{L}\p{N}\s]/gu)?.length || 0;
    const words = line.match(/[\p{L}\p{N}]{2,}/gu) || [];
    const usefulWords = words.filter((word) => (
      word.length >= 3 || /^(09|\d+|la|il|lo|le|un|una|dei|del|nel|non)$/i.test(word)
    ));
    const upperNoiseWords = words.filter((word) => (
      word.length <= 3 && word === word.toUpperCase() && /[A-Z]/.test(word)
    ));
    const lettersRatio = lettersAndNumbers / Math.max(1, line.length);

    return lettersAndNumbers >= 2
      && lettersRatio >= 0.45
      && usefulWords.length >= 2
      && upperNoiseWords.length < usefulWords.length
      && symbolCount <= Math.max(5, lettersAndNumbers * 0.45);
  })
  .join('\n')
  .trim();

const cleanKnownQuizText = (value = '') => value
  .replace(/\r/g, '')
  .split('\n')
  .map((line) => line.replace(/\s+/g, ' ').trim())
  .filter(Boolean)
  .join('\n')
  .trim();

const getReliableOcrText = (ocrData = {}) => {
  const lines = Array.isArray(ocrData.lines) ? ocrData.lines : [];

  if (!lines.length) {
    return cleanOcrText(ocrData.text || '');
  }

  const reliableLines = lines
    .map((line) => ({
      text: cleanOcrText(line.text || ''),
      confidence: Number(line.confidence ?? 0),
    }))
    .filter((line) => {
      if (!line.text) {
        return false;
      }

      const words = line.text.match(/[\p{L}\p{N}]{2,}/gu) || [];
      const hasKnownItalianShape = /\b(quale|seguenti|simbolo|logico|fondamentale|alfabeto|logica|ordine|nessuna|altre|opzioni|negazione|congiunzione|uguaglianza)\b/i.test(line.text);
      const minimumConfidence = hasKnownItalianShape ? 18 : 32;

      return words.length >= 2 && line.confidence >= minimumConfidence;
    })
    .map((line) => line.text);

  return cleanOcrText(reliableLines.join('\n') || ocrData.text || '');
};

const getOcrTextScore = (value = '') => {
  const cleanText = cleanOcrText(value);
  const lines = cleanText.split('\n').filter(Boolean);
  const words = cleanText.match(/[\p{L}\p{N}]{2,}/gu) || [];
  const longWords = words.filter((word) => word.length >= 4);
  const questionSignals = cleanText.match(/\b(quale|seguenti|simbolo|logico|fondamentale|alfabeto|logica|ordine|opzioni|negazione|congiunzione|uguaglianza)\b/gi) || [];
  const punctuationSignals = cleanText.match(/[?]/g) || [];
  const noisyLines = lines.filter((line) => {
    const lettersAndNumbers = line.match(/[\p{L}\p{N}]/gu)?.length || 0;
    const symbolCount = line.match(/[^\p{L}\p{N}\s]/gu)?.length || 0;

    return symbolCount > lettersAndNumbers;
  }).length;
  const veryShortLines = lines.filter((line) => line.length > 0 && line.length < 4).length;
  const averageLineLength = lines.length
    ? lines.reduce((total, line) => total + line.length, 0) / lines.length
    : 0;

  return (words.length * 2)
    + longWords.length
    + (questionSignals.length * 4)
    + (punctuationSignals.length * 2)
    + Math.min(12, averageLineLength / 8)
    - (noisyLines * 5)
    - (veryShortLines * 3);
};

const clampChannel = (value) => Math.max(0, Math.min(255, value));
const ocrPageSegmentationModes = [
  { mode: '6', label: 'single-block' },
  { mode: '4', label: 'single-column' },
  { mode: '11', label: 'sparse' },
];

const canvasToPngBlob = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (!blob) {
      reject(new Error('Invalid preprocessed image'));
      return;
    }

    resolve(blob);
  }, 'image/png', 0.96);
});

const getOtsuThreshold = (grayscaleValues) => {
  const histogram = new Array(256).fill(0);

  grayscaleValues.forEach((value) => {
    histogram[Math.round(clampChannel(value))] += 1;
  });

  const total = grayscaleValues.length;
  const sum = histogram.reduce((currentSum, count, index) => currentSum + (index * count), 0);
  let backgroundWeight = 0;
  let backgroundSum = 0;
  let maxVariance = 0;
  let threshold = 160;

  for (let index = 0; index < histogram.length; index += 1) {
    backgroundWeight += histogram[index];

    if (!backgroundWeight) {
      continue;
    }

    const foregroundWeight = total - backgroundWeight;

    if (!foregroundWeight) {
      break;
    }

    backgroundSum += index * histogram[index];
    const backgroundMean = backgroundSum / backgroundWeight;
    const foregroundMean = (sum - backgroundSum) / foregroundWeight;
    const variance = backgroundWeight * foregroundWeight * (backgroundMean - foregroundMean) ** 2;

    if (variance > maxVariance) {
      maxVariance = variance;
      threshold = index;
    }
  }

  return threshold;
};

const buildOcrVariant = async (sourceCanvas, transformPixel, name) => {
  const canvas = document.createElement('canvas');
  canvas.width = sourceCanvas.width;
  canvas.height = sourceCanvas.height;

  const context = canvas.getContext('2d');
  context.drawImage(sourceCanvas, 0, 0);

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;
  const grayscaleValues = [];

  for (let index = 0; index < data.length; index += 4) {
    grayscaleValues.push((data[index] * 0.299) + (data[index + 1] * 0.587) + (data[index + 2] * 0.114));
  }

  const threshold = getOtsuThreshold(grayscaleValues);

  for (let index = 0; index < data.length; index += 4) {
    const luminance = grayscaleValues[index / 4];
    const normalized = transformPixel(luminance, threshold);

    data[index] = normalized;
    data[index + 1] = normalized;
    data[index + 2] = normalized;
    data[index + 3] = 255;
  }

  context.putImageData(imageData, 0, 0);

  return {
    name,
    image: await canvasToPngBlob(canvas),
  };
};

const cropCanvasToInk = async (sourceCanvas, name) => {
  const sourceContext = sourceCanvas.getContext('2d');
  const imageData = sourceContext.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
  const { data } = imageData;
  let minX = sourceCanvas.width;
  let minY = sourceCanvas.height;
  let maxX = 0;
  let maxY = 0;
  let inkPixels = 0;

  for (let y = 0; y < sourceCanvas.height; y += 1) {
    for (let x = 0; x < sourceCanvas.width; x += 1) {
      const index = ((y * sourceCanvas.width) + x) * 4;
      const luminance = (data[index] * 0.299) + (data[index + 1] * 0.587) + (data[index + 2] * 0.114);
      const colorSpread = Math.max(data[index], data[index + 1], data[index + 2]) - Math.min(data[index], data[index + 1], data[index + 2]);

      if (luminance < 115 || (luminance < 145 && colorSpread > 22)) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
        inkPixels += 1;
      }
    }
  }

  if (inkPixels < 80 || minX >= maxX || minY >= maxY) {
    return null;
  }

  const paddingX = Math.round(sourceCanvas.width * 0.025);
  const paddingY = Math.round(sourceCanvas.height * 0.045);
  const cropX = Math.max(0, minX - paddingX);
  const cropY = Math.max(0, minY - paddingY);
  const cropWidth = Math.min(sourceCanvas.width - cropX, (maxX - minX) + (paddingX * 2));
  const cropHeight = Math.min(sourceCanvas.height - cropY, (maxY - minY) + (paddingY * 2));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, cropWidth);
  canvas.height = Math.max(1, cropHeight);

  const context = canvas.getContext('2d');
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(
    sourceCanvas,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
    0,
    0,
    canvas.width,
    canvas.height
  );

  return {
    name,
    image: await canvasToPngBlob(canvas),
  };
};

const preprocessImageForOcr = async (imageFile) => {
  const imageBitmap = await createImageBitmap(imageFile);
  const targetWidth = 2600;
  const maxWidth = 3200;
  const scale = Math.min(maxWidth / imageBitmap.width, Math.max(1, targetWidth / imageBitmap.width));
  const width = Math.round(imageBitmap.width * scale);
  const height = Math.round(imageBitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(imageBitmap, 0, 0, width, height);

  const sourceImageData = context.getImageData(0, 0, width, height);
  const { data } = sourceImageData;
  let luminanceSum = 0;

  for (let index = 0; index < data.length; index += 4) {
    luminanceSum += (data[index] * 0.299) + (data[index + 1] * 0.587) + (data[index + 2] * 0.114);
  }

  const averageLuminance = luminanceSum / (data.length / 4);
  const shouldInvert = averageLuminance < 128;

  for (let index = 0; index < data.length; index += 4) {
    let luminance = (data[index] * 0.299) + (data[index + 1] * 0.587) + (data[index + 2] * 0.114);

    if (shouldInvert) {
      luminance = 255 - luminance;
    }

    const normalized = clampChannel(((luminance - 128) * 1.65) + 148);
    data[index] = normalized;
    data[index + 1] = normalized;
    data[index + 2] = normalized;
  }

  context.putImageData(sourceImageData, 0, 0);
  imageBitmap.close?.();

  const variants = [
    {
      name: 'original',
      image: imageFile,
    },
    {
      name: 'enhanced',
      image: await canvasToPngBlob(canvas),
    },
    await buildOcrVariant(
      canvas,
      (luminance) => clampChannel(((luminance - 132) * 1.35) + 158),
      'soft-contrast'
    ),
    await cropCanvasToInk(canvas, 'content-crop'),
  ];

  return variants.filter(Boolean);
};

const loadImageFromUrl = (imageUrl) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = reject;
  image.src = imageUrl;
});

const Scanner = () => {
  const { t } = useLanguage();
  const text = t.scannerPage;
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const recognizedTextareaRef = useRef(null);
  const cropImageRef = useRef(null);
  const cropDragRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const answerQueryIdRef = useRef(1);

  const [archives, setArchives] = useState([]);
  const [subjectsByArchiveId, setSubjectsByArchiveId] = useState({});
  const [questions, setQuestions] = useState([]);
  const [selectedArchiveId, setSelectedArchiveId] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [questionTypeFilter, setQuestionTypeFilter] = useState('both');
  const [searchAnswers, setSearchAnswers] = useState(false);
  const [similaritySearch, setSimilaritySearch] = useState(false);
  const [recognizedText, setRecognizedText] = useState('');
  const [answerQueries, setAnswerQueries] = useState([{ id: 'answer-1', value: '' }]);
  const [submittedSearchQuery, setSubmittedSearchQuery] = useState('');
  const [submittedQuestionQuery, setSubmittedQuestionQuery] = useState('');
  const [submittedAnswerQueries, setSubmittedAnswerQueries] = useState([]);
  const [activeQueryIndex, setActiveQueryIndex] = useState(0);
  const [visibleAnswersByQuestionId, setVisibleAnswersByQuestionId] = useState({});
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isOcrLoading, setIsOcrLoading] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [error, setError] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [isCameraStarting, setIsCameraStarting] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState('');
  const [cropRect, setCropRect] = useState({ x: 0, y: 0, width: 100, height: 100 });
  const [showResultsModal, setShowResultsModal] = useState(false);

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const storageKey = useMemo(() => (
    `${scannerFiltersStorageKey}.${getTokenUserId(token)}`
  ), [token]);
  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
  }), [token]);

  useEffect(() => () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }

    if (cropImageUrl) {
      URL.revokeObjectURL(cropImageUrl);
    }
  }, []);

  useEffect(() => () => {
    if (cropImageUrl) {
      URL.revokeObjectURL(cropImageUrl);
    }
  }, [cropImageUrl]);

  useEffect(() => {
    if (!error) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setError('');
    }, 15000);

    return () => clearTimeout(timeoutId);
  }, [error]);

  const resizeRecognizedTextarea = useCallback(() => {
    const textarea = recognizedTextareaRef.current;

    if (!textarea) {
      return;
    }

    const maxHeight = window.innerWidth < 768 ? 220 : 300;
    textarea.style.height = 'auto';
    textarea.style.overflowY = 'hidden';

    const nextHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${Math.max(nextHeight, 132)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    resizeRecognizedTextarea();
  }, [recognizedText, resizeRecognizedTextarea]);

  useEffect(() => {
    window.addEventListener('resize', resizeRecognizedTextarea);

    return () => window.removeEventListener('resize', resizeRecognizedTextarea);
  }, [resizeRecognizedTextarea]);

  useEffect(() => {
    if (!token) {
      setIsLoadingData(false);
      return;
    }

    setIsLoadingData(true);
    setError('');

    Promise.all([
      axios.post(`${apiUrl}/getArchives`, {}, { headers: authHeaders }),
      axios.post(`${apiUrl}/scanner/questions`, {}, { headers: authHeaders }),
    ])
      .then(async ([archivesResponse, questionsResponse]) => {
        const nextArchives = archivesResponse.data || [];
        const subjectEntries = await Promise.all(nextArchives.map(async (archive) => {
          const subjectsResponse = await axios.post(
            `${apiUrl}/getSubjects`,
            { archiveId: archive._id },
            { headers: authHeaders }
          );

          return [archive._id, subjectsResponse.data || []];
        }));

        setArchives(nextArchives);
        setSubjectsByArchiveId(Object.fromEntries(subjectEntries));
        const storedFilters = readStoredScannerFilters(storageKey);
        const storedArchiveExists = nextArchives.some((archive) => archive._id === storedFilters.archiveId);
        const nextArchiveId = storedArchiveExists ? storedFilters.archiveId : '';
        const nextSubjects = nextArchiveId
          ? Object.fromEntries(subjectEntries)[nextArchiveId] || []
          : [];
        const storedSubjectExists = nextSubjects.some((subject) => subject._id === storedFilters.subjectId);

        setSelectedArchiveId(nextArchiveId);
        setSelectedSubjectId(storedSubjectExists ? storedFilters.subjectId : '');
        setQuestionTypeFilter(['both', 'closed', 'open'].includes(storedFilters.questionType)
          ? storedFilters.questionType
          : 'both');

        const scannerQuestions = questionsResponse.data || [];
        const subjectIdsMissingKeywords = [
          ...new Set(scannerQuestions
            .filter((question) => (
              !Object.prototype.hasOwnProperty.call(question, 'keywordHint')
              && !Object.prototype.hasOwnProperty.call(question, 'keyword_hint')
            ))
            .map((question) => question.subjectId)
            .filter(Boolean)),
        ];
        const keywordMetadataByQuestionId = new Map();

        await Promise.all(subjectIdsMissingKeywords.map(async (subjectId) => {
          try {
            const subjectQuestionsResponse = await axios.post(
              `${apiUrl}/getQuestions`,
              { subjectId },
              { headers: authHeaders }
            );

            (subjectQuestionsResponse.data || []).forEach((question) => {
              if (
                question.keywordHint
                || question.keyword_hint
                || question.keywordHintDisabled !== undefined
                || question.keyword_hint_disabled !== undefined
              ) {
                keywordMetadataByQuestionId.set(question._id, {
                  keywordHint: question.keywordHint || question.keyword_hint || '',
                  keywordHintDisabled: toScannerBooleanFlag(question.keywordHintDisabled || question.keyword_hint_disabled),
                });
              }
            });
          } catch (keywordRequestError) {
            console.warn('Unable to load scanner keyword hints:', keywordRequestError);
          }
        }));

        setQuestions(scannerQuestions.map((question) => ({
          ...question,
          keywordHint: question.keywordHint || question.keyword_hint || keywordMetadataByQuestionId.get(question._id)?.keywordHint || '',
          keywordHintDisabled: toScannerBooleanFlag(
            question.keywordHintDisabled
            || question.keyword_hint_disabled
            || keywordMetadataByQuestionId.get(question._id)?.keywordHintDisabled
          ),
          answerSearchText: (question.answers || []).map(getAnswerText).join(' '),
          normalizedQuestionText: normalizeText(question.questionText || ''),
          normalizedAnswerSearchText: normalizeText((question.answers || []).map(getAnswerText).join(' ')),
        })));
      })
      .catch((requestError) => {
        setError(requestError.response?.data?.message || requestError.message || text.errors.generic);
      })
      .finally(() => {
        setIsLoadingData(false);
      });
  }, [authHeaders, storageKey, text.errors.generic, token]);

  const subjectsForSelectedArchive = useMemo(() => (
    selectedArchiveId ? subjectsByArchiveId[selectedArchiveId] || [] : []
  ), [selectedArchiveId, subjectsByArchiveId]);

  useEffect(() => {
    if (!selectedSubjectId) {
      return;
    }

    const subjectExists = subjectsForSelectedArchive.some((subject) => subject._id === selectedSubjectId);

    if (!subjectExists) {
      setSelectedSubjectId('');
    }
  }, [selectedArchiveId, selectedSubjectId, subjectsForSelectedArchive]);

  useEffect(() => {
    if (!token || isLoadingData) {
      return;
    }

    setStoredValue(storageKey, JSON.stringify({
      archiveId: selectedArchiveId,
      subjectId: selectedSubjectId,
      questionType: questionTypeFilter,
    }));
  }, [isLoadingData, questionTypeFilter, selectedArchiveId, selectedSubjectId, storageKey, token]);

  const filteredQuestions = useMemo(() => questions.filter((question) => {
    if (selectedArchiveId && question.archiveId !== selectedArchiveId) {
      return false;
    }

    if (selectedSubjectId && question.subjectId !== selectedSubjectId) {
      return false;
    }

    if (questionTypeFilter !== 'both' && question.questionType !== questionTypeFilter) {
      return false;
    }

    return true;
  }), [questionTypeFilter, questions, selectedArchiveId, selectedSubjectId]);

  const recognizedQueries = useMemo(() => splitRecognizedQuestions(recognizedText), [recognizedText]);
  const activeRecognizedQuery = recognizedQueries[activeQueryIndex] || recognizedQueries[0] || '';
  const currentQuery = activeRecognizedQuery.trim();
  const draftSearchQuery = [
    currentQuery,
    ...(searchAnswers ? answerQueries.map((query) => query.value.trim()) : []),
  ].filter(Boolean).join(' ');

  useEffect(() => {
    if (activeQueryIndex > Math.max(recognizedQueries.length - 1, 0)) {
      setActiveQueryIndex(0);
    }
  }, [activeQueryIndex, recognizedQueries.length]);

  const results = useMemo(() => {
    const normalizedQuery = normalizeText(submittedSearchQuery);
    const normalizedQuestionQuery = normalizeText(submittedQuestionQuery);
    const normalizedAnswerQueries = submittedAnswerQueries.map(normalizeText).filter(Boolean);

    if (!normalizedQuery || normalizedQuery.length < 3) {
      return [];
    }

    const answerFilteredQuestions = filteredQuestions.filter((question) => normalizedAnswerQueries.every((query) => {
      const answers = (question.answers || []).map((answer) => normalizeText(getAnswerText(answer))).filter(Boolean);

      if (!similaritySearch) {
        return answers.some((answer) => answer.includes(query));
      }

      const answerFuse = new Fuse(answers, {
        includeScore: true,
        ignoreLocation: true,
        threshold: 0.42,
        minMatchCharLength: 3,
      });
      return answerFuse.search(query).length > 0;
    }));

    if (!similaritySearch) {
      return answerFilteredQuestions
        .filter((question) => !normalizedQuestionQuery
          || question.normalizedQuestionText?.includes(normalizedQuestionQuery))
        .slice(0, 12)
        .map((question) => ({
          ...question,
          similarity: null,
        }));
    }

    if (!normalizedQuestionQuery) {
      return answerFilteredQuestions.slice(0, 12).map((question) => ({ ...question, similarity: null }));
    }

    const questionFuse = new Fuse(answerFilteredQuestions, {
      includeScore: true,
      ignoreLocation: true,
      threshold: 0.42,
      minMatchCharLength: 3,
      keys: ['normalizedQuestionText'],
    });

    return questionFuse.search(normalizedQuestionQuery)
      .slice(0, 12)
      .map((result) => ({
        ...result.item,
        similarity: Math.max(0, Math.round((1 - (result.score || 0)) * 100)),
      }));
  }, [
    filteredQuestions,
    similaritySearch,
    submittedAnswerQueries,
    submittedQuestionQuery,
    submittedSearchQuery,
  ]);

  const improveOcrTextWithQuestionBank = useCallback((ocrText) => {
    const normalizedOcrText = normalizeText(ocrText);

    if (!normalizedOcrText || normalizedOcrText.length < 12 || !filteredQuestions.length) {
      return ocrText;
    }

    const localFuse = new Fuse(filteredQuestions, {
      includeScore: true,
      ignoreLocation: true,
      threshold: 0.55,
      minMatchCharLength: 3,
      keys: [
        { name: 'normalizedQuestionText', weight: 0.82 },
        { name: 'normalizedAnswerSearchText', weight: 0.18 },
      ],
    });
    const [bestMatch] = localFuse.search(normalizedOcrText);
    const similarity = bestMatch ? Math.max(0, 1 - (bestMatch.score || 0)) : 0;

    if (!bestMatch || similarity < 0.5) {
      return ocrText;
    }

    const questionText = String(bestMatch.item.questionText || '').trim();

    if (!questionText) {
      return ocrText;
    }

    const answerLines = (bestMatch.item.answers || [])
      .map(getAnswerText)
      .filter(Boolean);

    return cleanKnownQuizText([questionText, ...answerLines].join('\n'));
  }, [filteredQuestions]);

  const runOcr = async (imageFile) => {
    if (!imageFile || !imageFile.type?.startsWith('image/')) {
      setError(text.errors.invalidImage);
      return;
    }

    setError('');
    setIsOcrLoading(true);
    setOcrProgress(0);

    let worker = null;

    try {
      const ocrImages = await preprocessImageForOcr(imageFile);
      const totalAttempts = ocrImages.length * ocrPageSegmentationModes.length;
      let completedAttempts = 0;

      worker = await createWorker('ita+eng', 1, {
        logger: (message) => {
          if (message.status === 'recognizing text') {
            setOcrProgress(Math.round(((completedAttempts + (message.progress || 0)) / totalAttempts) * 100));
          }
        },
      });

      await worker.setParameters({
        preserve_interword_spaces: '1',
        user_defined_dpi: '300',
        tessedit_char_whitelist: '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyzÀÁÈÉÌÍÒÓÙÚàáèéìíòóùú.,;:?!\'"()- ',
      });

      const bestAttempt = {
        text: '',
        score: Number.NEGATIVE_INFINITY,
        variant: '',
        mode: '',
      };

      for (const pageSegmentationMode of ocrPageSegmentationModes) {
        await worker.setParameters({
          tessedit_pageseg_mode: pageSegmentationMode.mode,
        });

        for (const ocrImage of ocrImages) {
          const result = await worker.recognize(ocrImage.image);
          const attemptText = getReliableOcrText(result.data || {});
          const attemptScore = getOcrTextScore(attemptText);
          const confidenceBonus = Math.max(0, Number(result.data?.confidence || 0)) / 8;

          completedAttempts += 1;
          setOcrProgress(Math.round((completedAttempts / totalAttempts) * 100));

          if ((attemptScore + confidenceBonus) > bestAttempt.score) {
            bestAttempt.text = attemptText;
            bestAttempt.score = attemptScore + confidenceBonus;
            bestAttempt.variant = ocrImage.name;
            bestAttempt.mode = pageSegmentationMode.label;
          }
        }
      }

      await worker.terminate();
      worker = null;

      const nextText = improveOcrTextWithQuestionBank(bestAttempt.text);
      console.info(`Scanner OCR selected ${bestAttempt.variant}/${bestAttempt.mode}`, {
        score: bestAttempt.score,
      });

      if (!nextText) {
        setError(text.errors.emptyText);
      }

      setRecognizedText(nextText);
      setAnswerQueries((queries) => [{ ...(queries[0] || { id: 'answer-1' }), value: '' }]);
      setActiveQueryIndex(0);
    } catch (ocrError) {
      setError(ocrError.message || text.errors.ocrFailed);
    } finally {
      if (worker) {
        try {
          await worker.terminate();
        } catch (terminateError) {
          console.warn('Unable to terminate scanner OCR worker:', terminateError);
        }
      }

      setIsOcrLoading(false);
      setOcrProgress(0);
    }
  };

  const openCropEditor = (imageFile) => {
    if (!imageFile || !imageFile.type?.startsWith('image/')) {
      setError(text.errors.invalidImage);
      return;
    }

    setError('');
    setCropRect({ x: 0, y: 0, width: 100, height: 100 });
    setCropImageUrl((previousUrl) => {
      if (previousUrl) {
        URL.revokeObjectURL(previousUrl);
      }

      return URL.createObjectURL(imageFile);
    });
    setShowCropModal(true);
  };

  const closeCropEditor = () => {
    cropDragRef.current = null;
    setShowCropModal(false);
    setCropImageUrl('');
  };

  const getCropPointerPosition = (event) => {
    const image = cropImageRef.current;

    if (!image) {
      return null;
    }

    const bounds = image.getBoundingClientRect();
    const clientX = event.clientX ?? event.touches?.[0]?.clientX;
    const clientY = event.clientY ?? event.touches?.[0]?.clientY;

    if (clientX === undefined || clientY === undefined) {
      return null;
    }

    return {
      x: Math.min(100, Math.max(0, ((clientX - bounds.left) / bounds.width) * 100)),
      y: Math.min(100, Math.max(0, ((clientY - bounds.top) / bounds.height) * 100)),
    };
  };

  const startCropDrag = (event, mode) => {
    event.preventDefault();
    event.stopPropagation();

    const position = getCropPointerPosition(event);

    if (!position) {
      return;
    }

    cropDragRef.current = {
      mode,
      startPosition: position,
      startRect: cropRect,
    };
  };

  const updateCropDrag = (event) => {
    if (!cropDragRef.current) {
      return;
    }

    event.preventDefault();

    const position = getCropPointerPosition(event);

    if (!position) {
      return;
    }

    const { mode, startPosition, startRect } = cropDragRef.current;
    const deltaX = position.x - startPosition.x;
    const deltaY = position.y - startPosition.y;
    const minSize = 12;

    if (mode === 'move') {
      setCropRect({
        ...startRect,
        x: Math.min(100 - startRect.width, Math.max(0, startRect.x + deltaX)),
        y: Math.min(100 - startRect.height, Math.max(0, startRect.y + deltaY)),
      });
      return;
    }

    let nextX = startRect.x;
    let nextY = startRect.y;
    let nextWidth = startRect.width;
    let nextHeight = startRect.height;

    if (mode.includes('e')) {
      nextWidth = Math.min(100 - startRect.x, Math.max(minSize, startRect.width + deltaX));
    }

    if (mode.includes('s')) {
      nextHeight = Math.min(100 - startRect.y, Math.max(minSize, startRect.height + deltaY));
    }

    if (mode.includes('w')) {
      const maxDeltaX = startRect.width - minSize;
      const safeDeltaX = Math.min(maxDeltaX, Math.max(-startRect.x, deltaX));
      nextX = startRect.x + safeDeltaX;
      nextWidth = startRect.width - safeDeltaX;
    }

    if (mode.includes('n')) {
      const maxDeltaY = startRect.height - minSize;
      const safeDeltaY = Math.min(maxDeltaY, Math.max(-startRect.y, deltaY));
      nextY = startRect.y + safeDeltaY;
      nextHeight = startRect.height - safeDeltaY;
    }

    setCropRect({
      x: nextX,
      y: nextY,
      width: nextWidth,
      height: nextHeight,
    });
  };

  const stopCropDrag = () => {
    cropDragRef.current = null;
  };

  const processCroppedImage = async (useFullImage = false) => {
    if (!cropImageUrl) {
      setError(text.errors.invalidImage);
      return;
    }

    try {
      const image = await loadImageFromUrl(cropImageUrl);

      const rect = useFullImage ? { x: 0, y: 0, width: 100, height: 100 } : cropRect;
      const sourceX = Math.round((rect.x / 100) * image.naturalWidth);
      const sourceY = Math.round((rect.y / 100) * image.naturalHeight);
      const sourceWidth = Math.round((rect.width / 100) * image.naturalWidth);
      const sourceHeight = Math.round((rect.height / 100) * image.naturalHeight);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, sourceWidth);
      canvas.height = Math.max(1, sourceHeight);

      const context = canvas.getContext('2d');
      context.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        canvas.width,
        canvas.height
      );

      canvas.toBlob((blob) => {
        if (!blob) {
          setError(text.errors.invalidImage);
          return;
        }

        closeCropEditor();
        runOcr(new File([blob], 'scanner-crop.png', { type: 'image/png' }));
      }, 'image/png', 0.96);
    } catch (cropError) {
      setError(cropError.message || text.errors.invalidImage);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    openCropEditor(file);
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      cameraInputRef.current?.click();
      return;
    }

    setIsCameraStarting(true);
    setError('');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 3840 },
          height: { ideal: 2160 },
        },
        audio: false,
      });

      const [videoTrack] = stream.getVideoTracks();
      const capabilities = videoTrack?.getCapabilities?.() || {};
      const advancedConstraints = {};

      if (capabilities.focusMode?.includes?.('continuous')) {
        advancedConstraints.focusMode = 'continuous';
      }
      if (capabilities.exposureMode?.includes?.('continuous')) {
        advancedConstraints.exposureMode = 'continuous';
      }
      if (capabilities.whiteBalanceMode?.includes?.('continuous')) {
        advancedConstraints.whiteBalanceMode = 'continuous';
      }

      if (Object.keys(advancedConstraints).length) {
        await videoTrack.applyConstraints({ advanced: [advancedConstraints] }).catch(() => undefined);
      }

      streamRef.current = stream;
      setShowCamera(true);

      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play?.();
        }
      }, 0);
    } catch (cameraError) {
      setError(text.errors.cameraDenied);
    } finally {
      setIsCameraStarting(false);
    }
  };

  const closeCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    setShowCamera(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;

    if (!video || !video.videoWidth || !video.videoHeight) {
      setError(text.errors.invalidImage);
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) {
        setError(text.errors.invalidImage);
        return;
      }

      closeCamera();
      openCropEditor(new File([blob], 'scanner-capture.png', { type: 'image/png' }));
    }, 'image/png', 0.96);
  };

  const toggleAnswers = (questionId) => {
    setVisibleAnswersByQuestionId((previousState) => ({
      ...previousState,
      [questionId]: !previousState[questionId],
    }));
  };

  const openResultsModal = () => {
    const nextSearchQuery = draftSearchQuery.trim();

    if (!nextSearchQuery) {
      setError(text.emptySearch);
      return;
    }

    setSubmittedSearchQuery(nextSearchQuery);
    setSubmittedQuestionQuery(currentQuery);
    setSubmittedAnswerQueries(searchAnswers
      ? answerQueries.map((query) => query.value.trim()).filter(Boolean)
      : []);
    setShowResultsModal(true);
  };

  const addAnswerQuery = () => {
    answerQueryIdRef.current += 1;
    setAnswerQueries((queries) => [...queries, { id: `answer-${answerQueryIdRef.current}`, value: '' }]);
  };

  const updateAnswerQuery = (id, value) => {
    setAnswerQueries((queries) => queries.map((query) => (query.id === id ? { ...query, value } : query)));
  };

  const removeAnswerQuery = (id) => {
    setAnswerQueries((queries) => (queries.length > 1
      ? queries.filter((query) => query.id !== id)
      : [{ ...queries[0], value: '' }]));
  };

  return (
    <>
      <Row>
        <Col lg="12">
          <Card className="scanner-card" data-aos="fade-up">
            <Card.Body>
              <div className="scanner-header">
                <div>
                  <h3 className="mb-1">{text.title}</h3>
                  <p className="mb-0 text-muted">{text.subtitle}</p>
                </div>
                <Badge bg="primary" className="px-3 py-2">
                  {text.questionsLoaded(filteredQuestions.length)}
                </Badge>
              </div>

              {error && (
                <Alert variant="danger" className="mt-3" dismissible onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              <Row className="g-3 mt-1">
                <Col xl="12">
                  <div className="scanner-panel">
                    <div className="scanner-content">
                      <div className="scanner-filter-stack">
                        <Form.Group className="scanner-search-field">
                          <Form.Label>{text.archiveFilter}</Form.Label>
                          <Form.Select value={selectedArchiveId} onChange={(event) => setSelectedArchiveId(event.target.value)}>
                            <option value="">{text.allArchives}</option>
                            {archives.map((archive) => (
                              <option key={archive._id} value={archive._id}>{archive.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                        <Form.Group className="scanner-search-field">
                          <Form.Label>{text.subjectFilter}</Form.Label>
                          <Form.Select
                            value={selectedSubjectId}
                            disabled={!selectedArchiveId}
                            onChange={(event) => setSelectedSubjectId(event.target.value)}
                          >
                            <option value="">{text.allSubjects}</option>
                            {subjectsForSelectedArchive.map((subject) => (
                              <option key={subject._id} value={subject._id}>{subject.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                        <Form.Group className="scanner-search-field">
                          <Form.Label>{text.questionTypeFilter}</Form.Label>
                          <Form.Select
                            value={questionTypeFilter}
                            onChange={(event) => setQuestionTypeFilter(event.target.value)}
                          >
                            <option value="both">{text.allQuestionTypes}</option>
                            <option value="closed">{text.onlyClosedQuestions}</option>
                            <option value="open">{text.onlyOpenQuestions}</option>
                          </Form.Select>
                        </Form.Group>
                      </div>
                      <div className="scanner-filter-switch">
                        <Form.Check
                          type="switch"
                          id="scanner-search-answers"
                          checked={searchAnswers}
                          onChange={(event) => setSearchAnswers(event.target.checked)}
                          label={text.searchAnswers}
                        />
                      </div>

                      <div className="scanner-actions">
                        <Button variant="primary" onClick={startCamera} disabled={isOcrLoading || isCameraStarting}>
                          {isCameraStarting ? text.cameraStarting : text.takePhoto}
                        </Button>
                        <Button variant="soft-primary" onClick={() => fileInputRef.current?.click()} disabled={isOcrLoading}>
                          {text.uploadImage}
                        </Button>
                      </div>

                      <input ref={fileInputRef} type="file" accept="image/*" className="d-none" onChange={handleFileChange} />
                      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="d-none" onChange={handleFileChange} />

                      {isOcrLoading && (
                        <div className="scanner-loader">
                          <Spinner animation="border" variant="primary" />
                          <div className="flex-grow-1">
                            <div className="fw-semibold">{text.ocrLoading}</div>
                            <ProgressBar now={ocrProgress} label={`${ocrProgress}%`} />
                          </div>
                        </div>
                      )}

                      <Form.Group className="scanner-search-field mt-3">
                        <Form.Label>{text.recognizedText}</Form.Label>
                        <Form.Control
                          ref={recognizedTextareaRef}
                          as="textarea"
                          rows={4}
                          className="scanner-recognized-textarea"
                          value={recognizedText}
                          onChange={(event) => {
                            setRecognizedText(event.target.value);
                          }}
                          placeholder={text.recognizedPlaceholder}
                        />
                      </Form.Group>

                      {recognizedQueries.length > 1 && (
                        <div className="scanner-query-slider">
                          <Button
                            variant="soft-secondary"
                            size="sm"
                            disabled={activeQueryIndex === 0}
                            onClick={() => setActiveQueryIndex((index) => Math.max(0, index - 1))}
                          >
                            {text.previous}
                          </Button>
                          <div className="scanner-query-pills">
                            {recognizedQueries.map((query, index) => (
                              <button
                                key={`${query.slice(0, 16)}-${index}`}
                                type="button"
                                className={`scanner-query-pill ${activeQueryIndex === index ? 'is-active' : ''}`}
                                onClick={() => {
                                  setActiveQueryIndex(index);
                                }}
                              >
                                {index + 1}
                              </button>
                            ))}
                          </div>
                          <Button
                            variant="soft-secondary"
                            size="sm"
                            disabled={activeQueryIndex >= recognizedQueries.length - 1}
                            onClick={() => setActiveQueryIndex((index) => Math.min(recognizedQueries.length - 1, index + 1))}
                          >
                            {text.next}
                          </Button>
                        </div>
                      )}

                      {searchAnswers && (
                        <div className="scanner-answer-search mt-3">
                          <div className="scanner-answer-search-heading">
                            <Form.Label>{text.answerSearch}</Form.Label>
                            <Button
                              type="button"
                              variant="soft-primary"
                              size="sm"
                              className="scanner-answer-query-button"
                              onClick={addAnswerQuery}
                              title={text.addAnswerField}
                              aria-label={text.addAnswerField}
                            >
                              +
                            </Button>
                          </div>
                          {answerQueries.map((query, index) => (
                            <div className="scanner-answer-query-row" key={query.id}>
                              <Form.Control
                                as="textarea"
                                rows={1}
                                className="scanner-answer-textarea"
                                value={query.value}
                                onChange={(event) => updateAnswerQuery(query.id, event.target.value)}
                                placeholder={index === 0
                                  ? text.answerSearchPlaceholder
                                  : text.additionalAnswerSearchPlaceholder(index + 1)}
                                aria-label={text.answerSearchNumber(index + 1)}
                              />
                              <Button
                                type="button"
                                variant="soft-danger"
                                size="sm"
                                className="scanner-answer-query-button"
                                onClick={() => removeAnswerQuery(query.id)}
                                title={text.removeAnswerField}
                                aria-label={text.removeAnswerField}
                              >
                                &minus;
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="scanner-search-actions">
                        <div className="scanner-search-control">
                          <Button variant="primary" onClick={openResultsModal} disabled={isLoadingData || isOcrLoading}>
                            {text.searchButton}
                          </Button>
                          <Form.Check
                            type="switch"
                            id="scanner-similarity-search"
                            checked={similaritySearch}
                            onChange={(event) => setSimilaritySearch(event.target.checked)}
                            label={similaritySearch ? text.similaritySearch : text.exactSearch}
                          />
                          <small className="scanner-search-mode-help">
                            {similaritySearch ? text.similaritySearchOnHelp : text.similaritySearchOffHelp}
                          </small>
                        </div>
                        {isLoadingData && (
                          <div className="scanner-inline-loading">
                            <Spinner animation="border" size="sm" variant="primary" />
                            <span>{text.loadingQuestions}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showCamera} onHide={closeCamera} centered size="lg" fullscreen="md-down" className="scanner-camera-modal">
        <Modal.Header closeButton>
          <Modal.Title>{text.cameraTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <video ref={videoRef} className="scanner-video" playsInline muted />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="soft-secondary" onClick={closeCamera}>{text.close}</Button>
          <Button variant="primary" onClick={capturePhoto}>{text.capture}</Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showCropModal}
        onHide={closeCropEditor}
        centered
        scrollable
        size="xl"
        fullscreen="md-down"
        className="scanner-crop-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{text.cropTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">{text.cropHelp}</p>
          <div
            className="scanner-crop-stage"
            onPointerMove={updateCropDrag}
            onPointerUp={stopCropDrag}
            onPointerLeave={stopCropDrag}
          >
            {cropImageUrl && (
              <div className="scanner-crop-image-wrap">
                <img
                  ref={cropImageRef}
                  src={cropImageUrl}
                  alt={text.cropTitle}
                  className="scanner-crop-image"
                  draggable="false"
                />
                <div
                  className="scanner-crop-box"
                  style={{
                    left: `${cropRect.x}%`,
                    top: `${cropRect.y}%`,
                    width: `${cropRect.width}%`,
                    height: `${cropRect.height}%`,
                  }}
                  onPointerDown={(event) => startCropDrag(event, 'move')}
                >
                  <span className="scanner-crop-handle is-nw" onPointerDown={(event) => startCropDrag(event, 'nw')} />
                  <span className="scanner-crop-handle is-ne" onPointerDown={(event) => startCropDrag(event, 'ne')} />
                  <span className="scanner-crop-handle is-sw" onPointerDown={(event) => startCropDrag(event, 'sw')} />
                  <span className="scanner-crop-handle is-se" onPointerDown={(event) => startCropDrag(event, 'se')} />
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="soft-secondary" onClick={closeCropEditor}>{text.close}</Button>
          <Button variant="soft-primary" onClick={() => processCroppedImage(true)}>{text.useFullImage}</Button>
          <Button variant="primary" onClick={() => processCroppedImage(false)}>{text.useCrop}</Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showResultsModal}
        onHide={() => setShowResultsModal(false)}
        size="xl"
        centered
        scrollable
        className="scanner-results-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{text.resultsTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="scanner-active-query mt-0">
            <small className="text-muted">{text.currentSearch}</small>
            <p className="mb-0">{submittedSearchQuery || text.noCurrentSearch}</p>
          </div>

          {!submittedSearchQuery.trim() ? (
            <Alert variant="info" className="mt-3 mb-0">{text.emptySearch}</Alert>
          ) : results.length ? (
            <>
              <div className="scanner-results-summary">
                {text.resultsFound(results.length)}
              </div>
              <div className="scanner-results">
                {results.map((result) => {
                  const correctAnswers = getCorrectAnswers(result.answers);
                  const showAllAnswers = visibleAnswersByQuestionId[result._id];
                  const answersToShow = showAllAnswers ? result.answers || [] : correctAnswers;
                  const savedKeywordTerms = getScannerKeywordTerms(result.keywordHint || result.keyword_hint || '');
                  const keywordTerms = savedKeywordTerms.length
                    ? savedKeywordTerms
                    : getScannerAutomaticKeywordTerms(result);

                  return (
                    <div className="scanner-result" key={result._id}>
                      <div className="scanner-result-top">
                        <Badge bg={result.questionType === 'open' ? 'warning' : 'primary'}>
                          {result.questionType === 'open' ? text.openQuestion : text.closedQuestion}
                        </Badge>
                        {result.similarity === null ? (
                          <Badge bg="soft-primary" text="primary">{text.exactMatch}</Badge>
                        ) : (
                          <Badge bg="primary">{result.similarity}%</Badge>
                        )}
                        <span>{result.archiveName || text.noArchiveName}</span>
                        <span>{result.subjectName}</span>
                      </div>
                      <h5 className="scanner-question-text">{result.questionText || text.questionWithoutText}</h5>
                      <div className="scanner-answer-list">
                        {answersToShow.length ? answersToShow.map((answer, index) => (
                          <div
                            key={`${getAnswerText(answer)}-${index}`}
                            className={`scanner-answer ${answer.isCorrect ? 'is-correct' : ''}`}
                          >
                            {answer.isCorrect && <span className="scanner-answer-badge">{text.correct}</span>}
                            <span>
                              {renderScannerHighlightedText(getAnswerText(answer) || text.emptyAnswer, keywordTerms)}
                            </span>
                          </div>
                        )) : (
                          <div className="scanner-answer is-empty">{text.noCorrectAnswer}</div>
                        )}
                      </div>
                      {(result.answers || []).length > correctAnswers.length && (
                        <Button
                          variant="soft-primary"
                          size="sm"
                          onClick={() => toggleAnswers(result._id)}
                        >
                          {showAllAnswers ? text.hideAnswers : text.showAllAnswers}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <Alert variant="warning" className="mt-3 mb-0">{text.noResults}</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResultsModal(false)}>
            {text.close}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Scanner;
